import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Button,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, CircularProgress, Snackbar, Alert
} from '@mui/material';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import axios from 'axios';

interface Region {
  id: number;
  name: string;
  created_at: string;
  updated_at: string;
}

interface SnackbarState {
  open: boolean;
  message: string;
  severity: 'success' | 'error' | 'info' | 'warning';
}

const RegionsManager: React.FC = () => {
  const [regions, setRegions] = useState<Region[]>([]);
  const [openForm, setOpenForm] = useState(false);
  const [newRegion, setNewRegion] = useState({ name: '' });
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [regionToDelete, setRegionToDelete] = useState<Region | null>(null);
  const [error, setError] = useState('');
  const [snackbar, setSnackbar] = useState<SnackbarState>({
    open: false,
    message: '',
    severity: 'success'
  });

  useEffect(() => {
    document.title = 'Управление регионами | Zerde';
    fetchRegions();
  }, []);

  const showNotification = (message: string, severity: 'success' | 'error' | 'info' | 'warning') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const logAction = async (action: string) => {
    const currentRole = localStorage.getItem('role') || 'unknown';
    const currentUsername = localStorage.getItem('username') || 'unknown';
    try {
      await axios.post('https://tbl.zerdegroup.kz/api/log', {
        role: currentRole,
        username: currentUsername,
        action
      });
    } catch (error) {
      console.error('Ошибка при логировании действия:', error);
    }
  };

  const fetchRegions = async () => {
    try {
      const response = await axios.get('https://tbl.zerdegroup.kz/api/regions');
      if (response.data.success) {
        setRegions(response.data.data);
      } else {
        showNotification('Не удалось загрузить список регионов', 'error');
      }
    } catch (error) {
      console.error('Ошибка при получении списка регионов:', error);
      showNotification('Ошибка при загрузке регионов', 'error');
    }
  };

  const handleOpenDeleteDialog = (region: Region) => {
    setRegionToDelete(region);
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setRegionToDelete(null);
  };

  const confirmDelete = async () => {
    if (regionToDelete) {
      try {
        await axios.delete(`https://tbl.zerdegroup.kz/api/regions/${regionToDelete.id}`);
        showNotification(`Регион "${regionToDelete.name}" успешно удален`, 'success');
        await logAction(`удалил регион "${regionToDelete.name}" | Регионы`);
        fetchRegions();
      } catch (error) {
        console.error('Ошибка при удалении региона:', error);
        showNotification('Не удалось удалить регион', 'error');
      }
      handleCloseDeleteDialog();
    }
  };

  const handleOpenForm = (region?: Region) => {
    if (region) {
      setNewRegion({ name: region.name });
      setEditingId(region.id);
    } else {
      setNewRegion({ name: '' });
      setEditingId(null);
    }
    setOpenForm(true);
    setError('');
  };

  const handleCloseForm = () => {
    setOpenForm(false);
    setNewRegion({ name: '' });
    setError('');
    setEditingId(null);
  };

  const validateForm = (): boolean => {
    if (!newRegion.name.trim()) {
      setError('Название региона обязательно');
      return false;
    }
    return true;
  };

  const handleSubmitForm = async () => {
    if (!validateForm()) return;

    setLoading(true);
    try {
      if (editingId) {
        await axios.put(`https://tbl.zerdegroup.kz/api/regions/${editingId}`, newRegion);
        showNotification(`Регион "${newRegion.name}" успешно обновлен`, 'success');
        await logAction(`отредактировал регион "${newRegion.name}" | Регионы`);
      } else {
        await axios.post('https://tbl.zerdegroup.kz/api/regions', newRegion);
        showNotification(`Регион "${newRegion.name}" успешно создан`, 'success');
        await logAction(`добавил новый регион "${newRegion.name}" | Регионы`);
      }
      await fetchRegions();
      handleCloseForm();
    } catch (error: any) {
      if (error.response?.status === 400) {
        showNotification('Регион с таким названием уже существует', 'error');
      } else {
        showNotification('Не удалось сохранить регион', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="h5" sx={{ position: 'absolute', bottom: 15, right: 15 }}>
        <Button
          variant="contained"
          color="primary"
          onClick={() => handleOpenForm()}
          disabled={localStorage.getItem('role') === 'view'}
        >
          <AddIcon />
        </Button>
      </Typography>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Название</TableCell>
              <TableCell>Дата создания</TableCell>
              <TableCell>Последнее обновление</TableCell>
              <TableCell>Действия</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {regions.map((region) => (
              <TableRow key={region.id}>
                <TableCell>{region.id}</TableCell>
                <TableCell>{region.name}</TableCell>
                <TableCell>{new Date(region.created_at).toLocaleString()}</TableCell>
                <TableCell>{new Date(region.updated_at).toLocaleString()}</TableCell>
                <TableCell>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => handleOpenForm(region)}
                    sx={{ mr: 1 }}
                    disabled={localStorage.getItem('role') === 'view'}
                  >
                    <EditIcon />
                  </Button>
                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={() => handleOpenDeleteDialog(region)}
                    disabled={localStorage.getItem('role') === 'view'}
                  >
                    <DeleteForeverIcon />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={openForm} onClose={handleCloseForm} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ p: 1 }}>{editingId ? 'Редактировать регион' : 'Добавить новый регион'}</DialogTitle>
        <DialogContent sx={{ p: 1 }}>
          <TextField
            autoFocus
            margin="dense"
            label="Название региона"
            fullWidth
            value={newRegion.name}
            onChange={(e) => setNewRegion({ name: e.target.value })}
            error={!!error}
            helperText={error}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleSubmitForm}
            variant="contained"
            color="primary"
            disabled={loading || !newRegion.name.trim()}
          >
            {loading ? <CircularProgress size={24} /> : (editingId ? 'Обновить' : 'Создать')}
          </Button>
          <Button onClick={handleCloseForm} variant="outlined">
            Отмена
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openDeleteDialog} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Подтверждение удаления</DialogTitle>
        <DialogContent>
          <Typography>
            Вы уверены, что хотите удалить регион <b>{regionToDelete?.name}</b>?
            <br /> Действие нельзя отменить!
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} color="primary">
            <b>Отмена</b>
          </Button>
          <Button onClick={confirmDelete} color="error">
            Удалить
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default RegionsManager;